/* =========================================
   uci-discounts.js with chunking & concurrency
   ========================================= */

/* -----------------------------------------
   1) Utility: chunkArray
   ----------------------------------------- */
function chunkArray(array, size) {
  const chunks = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
}

/* -----------------------------------------
   2) Utility: processChunksInParallel
   -----------------------------------------
   - Takes an array of chunks
   - A function to handle each chunk (returns a Promise)
   - A concurrency limit
   - Processes up to 'concurrency' chunks at once
*/
async function processChunksInParallel(chunks, chunkHandler, concurrency) {
  const results = [];
  let currentIndex = 0;

  // Set of active promises
  const inFlight = new Set();

  // While we have chunks to process OR in-flight promises
  while (currentIndex < chunks.length || inFlight.size > 0) {
    // If we have room, launch a new chunk
    while (currentIndex < chunks.length && inFlight.size < concurrency) {
      const chunkIndex = currentIndex++;
      const chunk = chunks[chunkIndex];

      // Start the promise for this chunk
      const promise = chunkHandler(chunk).then((res) => {
        // Remove from inFlight once done
        inFlight.delete(promise);
        // Store result (if needed)
        results[chunkIndex] = res;
      });

      // Track this promise in inFlight
      inFlight.add(promise);
    }

    // If we still have promises in flight, wait for at least one to finish
    if (inFlight.size > 0) {
      await Promise.race(inFlight);
    }
  }

  return results;
}

/* -----------------------------------------
   3) fetchDiscountedPricesBulk (unchanged)
   -----------------------------------------
   - Single bulk request for an array of products
*/
async function fetchDiscountedPricesBulk(productsArray) {
  const requestBody = {
    products: productsArray.map((p) => ({
      product_id: p.productId,
      product_variant_id: p.variantId,
      price: p.originalPrice,
    })),
  };

  const bulkUrl = "https://shopify-discounts-dot-bsd-multi-loc-1.ue.r.appspot.com/api/discounted-prices-bulk";

  console.log("[fetchDiscountedPricesBulk] Sending bulk request:", requestBody);

  const response = await fetch(bulkUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    throw new Error(`Bulk fetch request failed. Status: ${response.status}`);
  }

  // Should return an array: [{ product_id, variant_id, final_price }, ...]
  return response.json();
}

/* -----------------------------------------
   4) Deal Zone Metafield Queue + Processing
   (unchanged)
   ----------------------------------------- */
const updateDealZoneQueue = new Map(); // Queue for pending Deal Zone updates

function queueDealZoneUpdate(productId, isDeal) {
  // If we already queued the same value, do nothing
  if (updateDealZoneQueue.has(productId) && updateDealZoneQueue.get(productId) === isDeal) {
    return;
  }
  updateDealZoneQueue.set(productId, isDeal);

  // Schedule the batch update if not already scheduled
  if (!updateDealZoneQueue.processing) {
    updateDealZoneQueue.processing = setTimeout(processDealZoneUpdates, 3000);
  }
}

function processDealZoneUpdates() {
  if (updateDealZoneQueue.size === 0) return;

  const updates = [];
  updateDealZoneQueue.forEach((isDeal, productId) => {
    updates.push({ product_id: productId, namespace: "custom", key: "deal_zone", value: isDeal });
  });

  updateDealZoneQueue.clear();
  delete updateDealZoneQueue.processing;

  console.log(`Updating Deal Zone metafields for ${updates.length} products...`);

  fetch("https://shopify-discounts-dot-bsd-multi-loc-1.ue.r.appspot.com/api/update-metafield", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ updates }),
  })
    .then((response) => response.json())
    .then((data) => console.log("Metafield updates successful:", data))
    .catch((error) => console.error("Error updating Deal Zone metafields:", error));
}

/* -----------------------------------------
   5) updatePriceDisplay (unchanged)
   ----------------------------------------- */
function updatePriceDisplay(element, originalPrice, discountedPrice, compareAtPrice) {
  const finalPriceElement = element.querySelector(".product-final-price");
  const originalPriceElement = element.querySelector(".product-original-price");
  const msrpElement = element.querySelector(".product-msrp");

  // Update final price
  if (finalPriceElement) {
    finalPriceElement.textContent = `$${discountedPrice.toFixed(2)}`;
  }

  // Show/hide "Was" price
  if (originalPriceElement) {
    if (originalPrice > discountedPrice) {
      originalPriceElement.innerHTML = `<span class="was-label">Was:</span> <span class="price-value">$${originalPrice.toFixed(
        2
      )}</span>`;
      originalPriceElement.style.display = "inline";
    } else {
      originalPriceElement.style.display = "none";
    }
  }

  // Show/hide "MSRP"
  if (msrpElement) {
    if (compareAtPrice > 0) {
      msrpElement.textContent = `MSRP: $${compareAtPrice.toFixed(2)}`;
      msrpElement.style.display = "inline";
    } else {
      msrpElement.style.display = "none";
    }
  }

  // If discount > ~4%, show sale badge
  const productId = element.dataset.productId;
  const discountPercentage = ((originalPrice - discountedPrice) / originalPrice) * 100;
  console.log(`[updatePriceDisplay] productId=${productId}, discountPercentage=${discountPercentage.toFixed(2)}`);

  // For your sale badge, we look up by #theme-sale-badge-wrapper-productId
  const saleBadgeWrapper = document.querySelector(`#theme-sale-badge-wrapper-${productId}`);
  const saleBadgeText = document.querySelector(`#theme-sale-badge-${productId} .js-badge-text`);

  if (saleBadgeWrapper && saleBadgeText) {
    if (discountPercentage > 3.99) {
      saleBadgeWrapper.style.display = "flex"; // or 'inline-flex'
      saleBadgeText.textContent = `Save ${discountPercentage.toFixed(0)}%`;
    } else {
      saleBadgeWrapper.style.display = "none";
    }
  } else {
    console.log("No sale badge wrapper found for productId:", productId);
  }
}

/* -----------------------------------------
   6) updatePrices - CHUNKED + LIMITED CONCURRENCY
   ----------------------------------------- */
async function updatePrices(context = document) {
  const priceElements = context.querySelectorAll("[data-product-id][data-variant-id][data-original-price]");

  // Gather products
  const productsToFetch = [];
  priceElements.forEach((el) => {
    const productId = el.dataset.productId;
    const variantId = el.dataset.variantId;
    const originalPrice = parseFloat(el.dataset.originalPrice);
    const compareAtPrice = parseFloat(el.dataset.compareAtPrice);

    if (!variantId || variantId === "null") {
      console.error(`Skipping price update for Product ID ${productId}. Missing or invalid variant ID.`);
      return;
    }

    // Mark element so we can skip changes in observers
    el.setAttribute("data-updated-by-script", "true");

    productsToFetch.push({
      productId,
      variantId,
      originalPrice,
      compareAtPrice,
      element: el,
    });
  });

  if (productsToFetch.length === 0) {
    console.log("No products found to update prices for.");
    processDealZoneUpdates(); // just in case
    return;
  }

  // 1) Chunk the products array by size=10
  const CHUNK_SIZE = 10;
  const chunks = chunkArray(productsToFetch, CHUNK_SIZE);
  console.log(`updatePrices: Splitting ${productsToFetch.length} products into ${chunks.length} chunk(s).`);

  // 2) Handler for a single chunk
  const handleChunk = async (chunk) => {
    // Build fetch array
    const fetchArray = chunk.map((info) => ({
      productId: info.productId,
      variantId: info.variantId,
      originalPrice: info.originalPrice,
    }));

    // Bulk request for this chunk
    const results = await fetchDiscountedPricesBulk(fetchArray);

    // Create a lookup
    const lookup = {};
    results.forEach((item) => {
      const key = `${item.product_id}-${item.variant_id}`;
      lookup[key] = item;
    });

    // Update UI
    chunk.forEach((info) => {
      const key = `${info.productId}-${info.variantId}`;
      const match = lookup[key];
      if (!match) {
        console.warn(`No discounted price found for key: ${key}`);
        updatePriceDisplay(info.element, info.originalPrice, info.originalPrice, info.compareAtPrice);
      } else {
        const discountedPrice = parseFloat(match.final_price);
        updatePriceDisplay(info.element, info.originalPrice, discountedPrice, info.compareAtPrice);

        const discountPercentage = ((info.originalPrice - discountedPrice) / info.originalPrice) * 100;
        const isDeal = discountPercentage > 3.99;
        queueDealZoneUpdate(info.productId, isDeal);
      }

      info.element.removeAttribute("data-updated-by-script");
    });

    return results; // optional return
  };

  try {
    // 3) Process all chunks in parallel, with concurrency=2
    const CONCURRENCY_LIMIT = 2;
    await processChunksInParallel(chunks, handleChunk, CONCURRENCY_LIMIT);
  } catch (error) {
    console.error("Bulk fetch error (chunked):", error);
    // Fallback: show original
    productsToFetch.forEach((info) => {
      updatePriceDisplay(info.element, info.originalPrice, info.originalPrice, info.compareAtPrice);
      info.element.removeAttribute("data-updated-by-script");
    });
  } finally {
    processDealZoneUpdates();
  }
}

/* -----------------------------------------
   7) DOMContentLoaded / Observers / Events
   ----------------------------------------- */
document.addEventListener("DOMContentLoaded", function () {
  console.log("Running initial price updates...");
  updatePrices(); // Initial price updates for the entire page

  /* ---------------
     Variant Picker
     --------------- */
  const variantPicker = document.querySelector("variant-picker");
  if (variantPicker) {
    // 1) Standard 'change' event
    variantPicker.addEventListener("change", () => {
      console.log("Variant changed (fallback). Updating prices (bulk)...");
      updatePrices();
    });

    // 2) Custom on:variant:change
    document.addEventListener("on:variant:change", (event) => {
      const { variant, product } = event.detail;
      console.log("Variant change detected:", variant);

      const priceElement = document.querySelector(`[data-product-id="${product.id}"]`);
      if (!priceElement) {
        console.warn("Price element not found for the selected variant. product.id:", product.id);
        return;
      }

      // Update dataset
      priceElement.dataset.variantId = variant.id;
      priceElement.dataset.originalPrice = (variant.price / 100).toFixed(2);
      priceElement.dataset.compareAtPrice = (variant.compare_at_price / 100).toFixed(2);

      console.log("Updated price-wrapper attributes:", {
        variantId: priceElement.dataset.variantId,
        originalPrice: priceElement.dataset.originalPrice,
        compareAtPrice: priceElement.dataset.compareAtPrice,
      });

      // Single-product approach (array of 1)
      const productId = product.id;
      const variantId = variant.id;
      const originalPrice = parseFloat(priceElement.dataset.originalPrice);
      const compareAtPrice = parseFloat(priceElement.dataset.compareAtPrice);

      priceElement.setAttribute("data-updated-by-script", "true");

      fetchDiscountedPricesBulk([{ productId, variantId, originalPrice }])
        .then((results) => {
          if (!results || !Array.isArray(results) || results.length === 0) {
            updatePriceDisplay(priceElement, originalPrice, originalPrice, compareAtPrice);
            return;
          }

          const discountedPrice = parseFloat(results[0].final_price) || originalPrice;
          updatePriceDisplay(priceElement, originalPrice, discountedPrice, compareAtPrice);

          const discountPercentage = ((originalPrice - discountedPrice) / originalPrice) * 100;
          const isDeal = discountPercentage > 3.99;
          queueDealZoneUpdate(productId, isDeal);
        })
        .catch((error) => {
          console.error(
            `Error fetching discounted price for Product ID ${productId}, Variant ID ${variantId}:`,
            error
          );
          updatePriceDisplay(priceElement, originalPrice, originalPrice, compareAtPrice);
        })
        .finally(() => {
          priceElement.removeAttribute("data-updated-by-script");
          processDealZoneUpdates();
        });
    });
  } else {
    console.warn("Variant picker not found. Ensure the selector is correct.");
  }

  /* -------------------------------------
     Listen for facet filter updates
     ------------------------------------- */
  document.addEventListener("on:facet-filters:updated", () => {
    console.log("Filters updated, running price updates...");
    updatePrices();
  });

  /* -------------------------------------
     Infinite Scroll Observer
     ------------------------------------- */
  const productGrid = document.getElementById("filter-results");
  if (productGrid) {
    const gridObserver = new MutationObserver((mutations) => {
      let newProducts = [];

      mutations.forEach((mutation) => {
        mutation.target.querySelectorAll("[data-product-id]").forEach((node) => {
          // If we haven't marked it as updated yet, mark & queue it
          if (!node.hasAttribute("data-price-updated")) {
            newProducts.push(node);
            node.setAttribute("data-price-updated", "true");
          }
        });
      });

      if (newProducts.length > 0) {
        console.log(`Detected ${newProducts.length} new products via infinite scroll. Updating prices...`);
        updatePrices();
      }
    });

    gridObserver.observe(productGrid, { childList: true, subtree: true });
    console.log("Infinite scroll observer attached.");
  }

  /* -------------------------------------
     Product Recommendations Observer
     ------------------------------------- */
  const recSection = document.querySelector("product-recommendations.section");
  if (recSection) {
    console.log("Attaching observer for product-recommendations section...");
    const recObserver = new MutationObserver((mutations) => {
      let newProducts = [];

      mutations.forEach((mutation) => {
        mutation.target.querySelectorAll("[data-product-id]").forEach((node) => {
          if (!node.hasAttribute("data-price-updated")) {
            newProducts.push(node);
            node.setAttribute("data-price-updated", "true");
          }
        });
      });

      if (newProducts.length > 0) {
        console.log(`Detected ${newProducts.length} new products in product-recommendations section. Updating prices...`);
        updatePrices(recSection);
      }
    });

    recObserver.observe(recSection, { childList: true, subtree: true });
  }

  /* --------------------------------------------------
     Compare Drawer/Modal Observers
     -------------------------------------------------- */
  const compareDrawer = document.querySelector(".js-compare-basket");
  const compareModal = document.querySelector(".js-compare-area");

  const observerCallback = (mutations) => {
    let shouldUpdate = false;
    mutations.forEach((mutation) => {
      const target = mutation.target;
      console.log("Mutation detected:", target);

      // Skip script-induced changes
      if (target.closest("[data-updated-by-script='true']")) {
        console.log("Skipping mutation caused by script.");
        return;
      }

      // If it's inside compare area/drawer, schedule an update
      if (target.closest(".js-compare-area") || target.closest(".compare-container")) {
        console.log("Relevant compare content updated. Updating prices...");
        shouldUpdate = true;
      }
    });

    if (shouldUpdate) {
      console.log("Re-running price updates...");
      updatePrices();
    }
  };

  const attachObserver = (element, description) => {
    if (element) {
      console.log(`Attaching observer to ${description}`);
      const observer = new MutationObserver(observerCallback);
      observer.observe(element, { childList: true, subtree: true });
    } else {
      console.warn(`Element not found: ${description}`);
    }
  };

  attachObserver(compareDrawer, "compare drawer");
  attachObserver(compareModal, "compare modal");

  /* -------------------------------------
     Quick Add Drawer Observer (ADDED)
     ------------------------------------- */
  const quickAddDrawer = document.querySelector("quick-add-drawer");
  attachObserver(quickAddDrawer, "quick add drawer");
});
